import { useState } from 'react';
import { ChevronDown, MessageSquare } from 'lucide-react';
import { RoomProvider, useThreads } from './liveblocks.config';
import { Composer, Thread } from "@liveblocks/react-ui";
import "@liveblocks/react-ui/styles.css";



const CommentsPanel = ({ isOpen, onClose }) => {
  const { threads, isLoading } = useThreads();

  // Add loading state
  if (isLoading) {
    return (
      <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'} z-50`}>
        <div className="p-6">
          <p className="text-gray-500">Loading comments..</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'} z-50`}>
      <div className="p-6 h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Comments</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            ✕
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {!threads || threads.length === 0 ? (
            <p className="text-gray-500 text-sm">No comments yet. Be the first!</p>
          ) : (
            threads.map((thread) => (
              <Thread key={thread.id} thread={thread} />
            ))
          )}
        </div>

        <div className="border-t pt-4">
          <Composer />
        </div>
      </div>
    </div>
  );
};


// Wrapper component that provides the room context
const OneChoiceWithComments = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);


  const pages = [
  // POETIC OPENING
  {
    content: `This is not a book.
It is the stillness before the question forms.
It will not guide you.
It will only stop performing long enough for you to hear your own voice returning.
There are no methods here.
Only mirrors, folded into paper.
If you read it, it may dissolve.
If you forget it, it may remain.
The real author has no name.
But you have met before.
It was there the moment you stopped asking.
And it is here, now, watching you turn the page.`,
    bg: 'from-indigo-950 to-indigo-900',
    large: false
  },
  { content: '', bg: 'from-indigo-900 to-slate-950', blank: true },
  
  // TITLE PAGE
  {
    content: `Applied Ontological Inquiry

Conscious Research for a Conscious Future



With James Breeze`,
    bg: 'from-slate-950 to-slate-900',
    large: true
  },
  { content: '', bg: 'from-slate-900 to-slate-800', blank: true },
  
  // THE INVITATION
  {
    content: `The Invitation: To a Different Kind of Inquiry`,
    bg: 'from-slate-800 to-amber-950',
    large: true
  },
  {
    content: `This journey has 3 Dimensions.

**The Intellectual Dimension:** This is the journey of grasping the concepts. You will learn about the history of research, the philosophical divides that have shaped our world, and the arguments for a new paradigm of inquiry.

**The Practical Dimension:** This is about application. You will find a playbook of tools, analogies, and frameworks to communicate complex ideas, execute better research, and make more insightful design decisions.

**The Personal Dimension:** This encourages transformation. At the end of each chapter, you will find "A Question for Your World." These are not rhetorical. They are prompts to turn the lens of inquiry upon yourself. This book's highest purpose is to serve as a mirror, helping you to see the hidden beliefs and mental models that shape your reality. The goal here is to facilitate your own inner-sight.

To get the most from this book, treat it not as a monologue to be consumed, but as a dialogue to be engaged with.`,
    bg: 'from-amber-950 to-amber-900'
  },
  { content: '', bg: 'from-amber-900 to-slate-900', blank: true },
  
  // HUMAN STUDIES RECLAIMED
  {
    content: `Human Studies, Reclaimed`,
    bg: 'from-slate-900 to-slate-800',
    large: true
  },
  {
    content: `This book is about people, not as subjects, but as creators of their own reality.

For too long, the study of human life has reduced itself to measuring what can be seen: behaviour, outputs, compliance. We've built academic machines to generate publications, not transformation. We've trained researchers to perform certainty, not innovation. In doing so, we've forgotten that humans are not objects of study. We are the field itself.

This book is grounded in Human Studies, the field beneath all fields. Not anthropology, psychology, or sociology, but the ground those disciplines stand on: the human capacity to think, believe, feel, and create. Before we measure behaviour, we live. And that living, messy, emotional, patterned, is where inner-sight begins.

Human Studies is the deep soil where belief systems grow. It inquires not just into what humans do, but why they do it, not as a search for fixed explanations, but as a witnessing of living patterns. This field doesn't compete with the others. It contains them. And it asks a deeper question:`,
    bg: 'from-slate-800 to-slate-700'
  },
  { content: '', bg: 'from-slate-700 to-slate-800', blank: true },
  {
    content: `What is the cost of not knowing ourselves?

This book returns to Human Studies as a living inquiry into how we construct and experience reality. It is both a definition and a departure. A definition, because it anchors the work. A departure, because it refuses to play by the rules that have kept human inner-sight trapped in method and performance.`,
    bg: 'from-slate-800 to-slate-900'
  },
  
  // THE COST OF THE PERFORMANCE
  {
    content: `The Cost of the Performance`,
    bg: 'from-slate-900 to-red-950',
    large: true
  },
  {
    content: `This theatre of proof is not a harmless academic exercise; its consequences are written into the code of our devices, the structure of our workplaces, and the fabric of our daily lives. The performance explains the central paradox of the modern world: we are drowning in data, yet starved for wisdom. We launch data-validated products that fail, not because the numbers were wrong, but because they answered the wrong questions from the start.

Consider the devices built to mediate our reality. Our phones, engineered for maximum engagement, are masterpieces of attention capture. The research that built them was focused on optimizing for clicks, views, and time-on-screen, quantifiable metrics that look great on a dashboard but ignore the qualitative reality of human presence. What person in their right mind wants to wake up and feel an immediate, anxious attachment to their phone? Yet this is the world we have built. Now, this same flawed paradigm is being extended to smart glasses. The failures of Google Glass and the inevitable struggles of Meta's glasses are not technological failures; they are failures of human insight. They are products of an inquiry that never bothered to ask what it feels like to be human in a world where the boundary between seeing, being manipulated and being recorded has collapsed. They are built for a scalable, data-rich future, without ever understanding the lived, human present.`,
    bg: 'from-red-950 to-red-900'
  },
  {
    content: `This disconnect is not confined to our devices; it defines the systems we navigate every day at work. We are surrounded by software and processes designed to be "usable" but that ultimately lock us into rigid workflows, stripping us of our volition. This fosters a culture of unconscious activity, where the path of least resistance is to accept subpar tools and develop workarounds for systemic issues, assuming "that's just the way things are." When people are forced to make these choices day after day, performing tasks that offer no sense of agency, the cumulative effect of these micro-interactions is toxic. They add up to create deep, systemic stress and burnout, an unconscious exhaustion born from a thousand tiny moments of powerlessness.

This brings us to our urgent, contemporary crisis: the rise of Artificial Intelligence. AI is the ultimate engine of speed and scale, the logical endpoint of a world that worships the quantifiable. If we are not careful, it will build a bigger, faster, and more convincing theatre of proof than we could ever imagine. In this new era, putting the human in the loop is not a suggestion; it is an imperative. As AI masters our routine tasks, the uniquely human capacity for inner-sight, for understanding belief, context, and meaning, is no longer a soft skill; it is the essential asset. This is the immediate relevance of this work.`,
    bg: 'from-red-900 to-red-800'
  },
  { content: '', bg: 'from-red-800 to-slate-900', blank: true },
  {
    content: `The choice is whether we will be the ghost in the machine or the consciousness that directs it.`,
    bg: 'from-slate-900 to-slate-800',
    pause: true
  },
  { content: '', bg: 'from-slate-800 to-slate-900', blank: true },
  
  // INVITATION TO INQUIRY
  {
    content: `An Invitation to a Different Kind of Inquiry`,
    bg: 'from-slate-900 to-blue-950',
    large: true
  },
  {
    content: `We have shown that much of what passes for research today is not a search for truth, but a performance of certainty staged in a theatre of proof. The cost is immense: wasted resources, failed innovations, and a deep disconnect from the human truths that drive everything.

But there is an alternative. The alternative is not a new method, but a new posture. It is the shift from listening to what people say to listening for the echo, the underlying belief system that makes their words make sense. We are not interested in the answer itself, but in the world that had to be true for that answer to be given.

This exploration is an invitation to leave the theatre. The first step is not to ask a better question of others, but to ask a different one of yourself: What performance am I a part of, and what truth lies just outside the stage lights?`,
    bg: 'from-blue-950 to-blue-900'
  },
  
  // PART 1 TITLE
  { content: '', bg: 'from-blue-900 to-purple-950', blank: true },
  {
    content: `PART 1

THE CRISIS OF BELIEVABILITY`,
    bg: 'from-purple-950 to-purple-900',
    large: true
  },
  { content: '', bg: 'from-purple-900 to-amber-950', blank: true },
  
  // CHAPTER 1
  {
    content: `Chapter 1

The Theatre of Proof`,
    bg: 'from-amber-950 to-amber-900',
    large: true
  },
  {
    content: `The most important decisions in our world are not based on truth. They are based on a performance.

For decades, the pursuit of knowledge has been presented as a grand stage play with two rival protagonists: the quantitative scientist, with their cast of numbers, and the qualitative researcher, with their deep, human stories. We have been led to believe they are competing for the prize of truth. This is a fiction. There is no genuine competition. There is only a theatre. In this theatre, research findings are not rivals competing for the truth; they are actors auditioning for a role in a high-stakes corporate or academic drama. The directors of this drama, the executives, the decision-makers, the academic committees, are not casting for the most truthful actor. They are casting for the most believable one. And believability, in this theatre, has nothing to do with truth and everything to do with managing the anxiety of, or pressure from, the audience.`,
    bg: 'from-amber-900 to-amber-800'
  },
  {
    content: `In this Theatre of Proof, two lead actors are always vying for the main role. The first is the bankable star: Quantitative Data. It arrives with a massive entourage, speaks in the simple, declarative language of numbers, and promises a straightforward, satisfying plot. Its performance is loud, confident, and, most importantly, easily defensible. It looks good on a poster.

The second actor is the intense, unknown character actor: Qualitative Insight. It doesn't have the fame or the entourage, but it delivers a performance of breathtaking depth and nuance. It reveals uncomfortable, challenging truths about the characters and the plot. It makes the audience shift in their seats. It is profound, but it is risky.

But there is a deeper layer they cannot reach. Before any behaviour is recorded, before any context is explained, there is a chain: first we think, then we filter that thought through a belief lens, which generates a feeling, and that feeling drives our behaviour. This is the origin point. And the theatre has no instrument to see it.`,
    bg: 'from-amber-800 to-amber-900'
  },
  {
    content: `The director's choice between them is rarely about artistic merit. It is about managing the critics. I learned this not in a boardroom, but in the quiet wings of this theatre, at a university. For my honours thesis in psychology, I cast a brilliant but unknown actor in the lead role: a study on meditation. At the time, it was a new and vital topic, and I poured everything into the performance, proving my hypotheses and creating a work of genuine insight. The directors of the show, the academic markers, had a choice: to award this performance a first-class honour, or a second. They chose the second. The reason had nothing to do with the quality of the work. As my supervisor privately admitted, the directors were afraid of the critics, their academic peers. They feared that if they gave the show's top award to a performance about a "soft" topic like meditation, they would be ridiculed. The mental model scripting their decision was pure fear. To protect their reputations, to remain comfortable in the established world of what was considered "serious" science, they sent the character actor home and left the award for a safer, more bankable star.`,
    bg: 'from-amber-900 to-red-950'
  },
  { content: '', bg: 'from-red-950 to-red-900', blank: true },
  {
    content: `It was my first lesson in the Theatre of Proof: a system's fear will always smother a truth it finds inconvenient.`,
    bg: 'from-red-900 to-red-800',
    pause: true
  },
  {
    content: `This dynamic is not just about dismissing inconvenient truths; it is also about embracing convenient performances. Years later, I was in a meeting with an executive at a global FMCG company. He needed to convince the board, a notoriously tough audience, of his position. He didn't bring a spreadsheet with thousands of data points. He didn't present a statistically significant study. He staged a one-act play. He put a single image on the screen: an eye gaze replay from one person's eye-tracking session. One person.

The effect was mesmerizing. The audience leaned in. Before them was an overlay of circles and lines on a picture of a supermarket shelf. The executive explained what they were seeing: each circle was a fixation, a moment where the customer's eye had stopped to consciously process information. The longer the eye paused, the larger the circle grew, a visible bloom of concentration. Connecting these fixations were thin lines called saccades, tracing the eye's frantic, near-instantaneous jumps from one point of interest to the next. The board was no longer looking at a simple diagram; they were witnessing the raw, silent monologue of a customer's attention. The visceral, undeniable drama of seeing the world through a single person's eyes, decoded with this visual authority, was a theatrical masterstroke. It bypassed their intellectual defenses and went straight to their gut. It was believable because it was a compelling spectacle. The executive won his argument not by presenting overwhelming evidence, but by staging the most convincing performance.`,
    bg: 'from-red-800 to-red-900'
  },
  { content: '', bg: 'from-red-900 to-slate-900', blank: true },
  {
    content: `This is the hypocrisy at the heart of the theatre. The rules of evidence are not fixed; they are bent or broken depending on the needs of the production. A profound insight from a dozen deep interviews can be dismissed as "anecdotal," while a single heat map can be hailed as "proof" if it performs its role effectively.

Consider the famous "F-Pattern" in user experience research done with eye tracking. It describes how people read search results on a web page, scanning across the top, then down the left side, then across again, forming a pattern that looks like the letter F. This research has influenced the design of countless websites and justified the high price of ads placed in those top "hot spots." It is treated as a fundamental law of online behaviour. But it is a fiction, a ghost created by the machine of the research process itself. The original studies must have used a "concurrent think-aloud" protocol. This means that as a person was looking at the search results, they were asked to simultaneously talk about what they were doing and thinking. I have tested this myself, many times, in usability testing sessions over the years. Of course, people look at the top of the page first, it's the top. But when someone is genuinely looking for something, when they are in their natural state of inquiry and not being asked to perform for a researcher, they don't just scan in an F-shape. They read the whole page. They go where their interest takes them.

The F-Pattern only appears consistently when you introduce the observer. The act of observation changes the behaviour. The F-Pattern is not a map of how people read; it is a map of how people read while being observed. It is an artifact of the inquiry, a shadow cast by the researcher's presence.`,
    bg: 'from-slate-900 to-slate-800'
  },
  { content: '', bg: 'from-slate-800 to-slate-900', blank: true },
  {
    content: `How did this theatre get built? The stage was set centuries ago. Thinkers like Aristotle once saw quality and quantity as a complementary cast of characters, two equally important ways of understanding a single, unified reality. But in the 17th century, in a desperate search for certainty, René Descartes rewrote the play. He declared that only one actor, the quantifiable number, was capable of playing the role of Truth. The qualitative story, with all its messy, human subjectivity, was demoted to a minor, unreliable character. This was an act of egotistical science, and it built the stage upon which our modern drama of defensibility is performed.

Today, we are all living in this theatre. We see organizations spend millions on quantitative surveys that do nothing more than rehearse a script they already wrote, confirming their own biases in a closed loop of self-assurance. These are not acts of discovery; they are expensive dress rehearsals. The supposed rivalry between the two research paradigms is merely a casting dispute, a manufactured drama to distract from the fact that the play itself is a fraud. It is a performance scripted by fear, directed by reputation, and staged for an audience that craves the comfort of a simple story.

The casualty of this performance is not one of the actors. The casualty is genuine inner-sight.`,
    bg: 'from-slate-900 to-slate-950'
  },
  { content: '', bg: 'from-slate-950 to-blue-950', blank: true },
  
  // CLOSING QUESTION
  {
    content: `A Question for Your World:`,
    bg: 'from-blue-950 to-blue-900',
    large: true
  },
  {
    content: `Where in your own work or life have you seen a deep, human truth dismissed because it couldn't be proven by a number?`,
    bg: 'from-blue-900 to-blue-800',
    pause: true
  },
  { content: '', bg: 'from-blue-800 to-slate-900', blank: true },
  {
    content: `The theatre only holds power as long as we forget we're watching a performance. What comes next is not another method to master. It is an invitation to a different kind of listening.

But before we can build that, we must understand how the stage was built, and why we keep returning to it.`,
    bg: 'from-slate-900 to-slate-950'
  },
];



  const currentPageData = pages[currentPage];
  const isLastPage = currentPage === pages.length - 1;

  return (
    <RoomProvider id={`onechoice-page-${currentPage}`}>
      <div className="min-h-screen w-full overflow-hidden">
        <div 
          className={`min-h-screen w-full bg-gradient-to-b ${currentPageData.bg} flex items-center justify-center transition-all duration-1000 ease-in-out p-6`}
        >
          <div className="max-w-2xl w-full pb-32">
            {currentPageData.blank ? (
              <div className="h-screen flex items-center justify-center">
                <ChevronDown className="w-8 h-8 text-gray-400 animate-bounce" />
              </div>
            ) : (
              <div className={`text-gray-100 ${currentPageData.large ? 'text-2xl md:text-4xl text-center' : 'text-base md:text-xl'} leading-relaxed whitespace-pre-line`}>
                {currentPageData.content}
              </div>
            )}
          </div>
        </div>

        {/* Comments Toggle Button */}
        <button
          onClick={() => setCommentsOpen(!commentsOpen)}
          className="fixed top-8 right-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-4 rounded-full transition-all duration-300 border border-white/20 z-40"
        >
          <MessageSquare className="w-5 h-5" />
        </button>

        {/* Comments Panel - now inside RoomProvider */}
        <CommentsPanel 
          isOpen={commentsOpen}
          onClose={() => setCommentsOpen(false)}
        />

        {/* Navigation */}
        <div className="fixed bottom-8 left-0 right-0 px-4">
          <div className="flex justify-between sm:justify-center items-center max-w-md sm:max-w-none mx-auto gap-3 sm:gap-4">
            {currentPage > 0 ? (
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-3 sm:px-6 sm:py-4 rounded-full transition-all duration-300 flex items-center gap-2 border border-white/20"
              >
                <ChevronDown className="w-4 h-4 rotate-180" />
                <span className="text-sm font-light">Back</span>
              </button>
            ) : (
              <div className="sm:hidden"></div>
            )}
            
            {!isLastPage && (
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-3 sm:px-8 sm:py-4 rounded-full transition-all duration-300 flex items-center gap-2 border border-white/20"
              >
                <span className="text-sm font-light">Continue</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="fixed top-4 right-4 text-white/40 text-xs">
          {currentPage + 1} / {pages.length}
        </div>
      </div>
    </RoomProvider>
  );
};

export default OneChoiceWithComments;